import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Kanban, Target } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageContainer, PageHeader, Segmented, Skeleton, EmptyState, InlineError, CompanyLogo, UserAvatar } from "@/components/common";
import { ScorePill, ValueRange, IntelStageChip, INTEL_STAGES, EvidenceLabel } from "@/components/intel/primitives";
import { useIntel } from "@/components/intel/context";

const STATUS = [{ key: "open", label: "Open" }, { key: "won", label: "Won" }, { key: "lost", label: "Lost" }, { key: "all", label: "All" }];

export default function IntelOpportunitiesPage() {
  const navigate = useNavigate();
  const intel = useIntel();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("open");
  const [busy, setBusy] = useState(null);

  const load = () => { setError(null); api.get("/intel/opportunities", { params: { status } }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))); };
  useEffect(() => { load(); }, [status, intel.version]); // eslint-disable-line react-hooks/exhaustive-deps

  const stages = data?.stages?.length ? data.stages : INTEL_STAGES;
  const rows = useMemo(() => data?.rows || [], [data]);
  const totals = useMemo(() => ({ low: rows.reduce((s, r) => s + (r.value_low || 0), 0), high: rows.reduce((s, r) => s + (r.value_high || 0), 0) }), [rows]);

  const setStage = async (o, stage) => {
    setBusy(o.id);
    try {
      await api.patch(`/intel/opportunities/${o.id}`, { stage });
      toast.success(`Moved to ${stages.find((s) => s.key === stage)?.label || stage}`);
      intel.bump();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const handoff = async (o) => {
    setBusy(o.id);
    try {
      const r = await api.post(`/intel/opportunities/${o.id}/handoff`);
      toast.success("Created in Commercial OS", { action: { label: "Open", onClick: () => navigate(`/opportunities/${r.data.opportunity_id}`) } });
      intel.bump();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const fmtM = (n) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M` : `${Math.round(n / 1000)}k`);

  return (
    <PageContainer wide data-testid="intel-opportunities-page">
      <PageHeader
        eyebrow="Sales Intelligence"
        title="Intel pipeline"
        description={data ? <span className="inline-flex flex-wrap items-center gap-x-2"><span><span className="font-semibold tabular text-foreground">{data.total}</span> {status === "all" ? "" : status} opportunities</span>{totals.high ? (<><span className="text-border">·</span><span>est. <span className="font-mono font-semibold tabular text-foreground">NOK {fmtM(totals.low)} - {fmtM(totals.high)}</span></span><EvidenceLabel kind="hypothesis" compact /></>) : null}</span> : "Compact list of pursued opportunities. Hand off to the Commercial OS when a deal is real."}
        actions={<Button variant="outline" className="gap-2" onClick={() => navigate("/pipeline")} data-testid="intel-opps-open-os-pipeline-button"><Kanban className="h-4 w-4" /> Commercial OS pipeline</Button>}
      />
      {error ? <InlineError onRetry={load} testId="intel-opps-error">{error}</InlineError> : null}
      <div className="mb-4"><Segmented items={STATUS} value={status} onChange={setStatus} testIdPrefix="intel-opps-status" /></div>

      {!data ? (
        <div className="surface-flat divide-y divide-black/[0.05]">{[0, 1, 2].map((i) => <div key={i} className="flex gap-4 px-5 py-4"><Skeleton className="h-9 w-9 rounded-lg" /><div className="flex-1"><Skeleton className="h-4 w-1/3" /><Skeleton className="mt-2 h-3 w-1/2" /></div></div>)}</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={Target} title="Nothing in the intel pipeline" description="Create an opportunity from Today, the radar or a company page when a hypothesis is worth pursuing." action={<Button onClick={() => navigate("/intel/today")}>Open Today</Button>} testId="intel-opps-empty" />
      ) : (
        <div className="surface-flat overflow-hidden" data-testid="intel-opps-list">
          <div className="hidden grid-cols-[1fr_190px_90px_130px_120px_110px_130px] items-center gap-4 border-b border-black/[0.06] bg-[hsl(40_12%_98%)] px-5 py-2 lg:grid">
            <span className="eyebrow text-[10px]">Opportunity</span>
            <span className="eyebrow text-[10px]">Stage</span>
            <span className="eyebrow text-right text-[10px]">Score</span>
            <span className="eyebrow text-right text-[10px]">Est. range</span>
            <span className="eyebrow text-[10px]">Owner</span>
            <span className="eyebrow text-[10px]">Updated</span>
            <span className="eyebrow text-right text-[10px]">Commercial OS</span>
          </div>
          <div className="divide-y divide-black/[0.05]">
            {rows.map((o) => (
              <div key={o.id} className="grid grid-cols-1 gap-3 px-5 py-3.5 lg:grid-cols-[1fr_190px_90px_130px_120px_110px_130px] lg:items-center lg:gap-4" data-testid="intel-opps-row">
                <div className="flex min-w-0 items-center gap-3.5">
                  <CompanyLogo name={o.company?.name} domain={o.company?.website} size="sm" />
                  <div className="min-w-0">
                    <Link to={`/intel/companies/${o.company_id}`} className="block truncate text-[14px] font-semibold tracking-[-0.01em] hover:text-primary" data-testid="intel-opps-row-name">{o.name}</Link>
                    <div className="truncate text-[12.5px] text-muted-foreground">{o.next_action || o.recommended_action}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 lg:block">
                  {o.status === "open" ? (
                    <Select value={o.stage} onValueChange={(v) => setStage(o, v)} disabled={busy === o.id}>
                      <SelectTrigger className="h-8 w-[180px] rounded-[9px] bg-white text-[12.5px]" data-testid="intel-opps-stage-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{stages.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <IntelStageChip stage={o.stage} stages={stages} />}
                </div>
                <div className="hidden justify-end lg:flex"><ScorePill score={o.score} breakdown={o.score_breakdown} /></div>
                <div className="hidden text-right text-[13px] lg:block"><ValueRange label={o.value_label} /></div>
                <div className="hidden items-center gap-2 text-[12.5px] text-muted-foreground lg:flex">{o.assignee ? <><UserAvatar name={o.assignee.name} initials={o.assignee.initials} color={o.assignee.color} src={o.assignee.avatar_url} /> <span className="truncate">{o.assignee.name?.split(" ")[0]}</span></> : "–"}</div>
                <div className="hidden font-mono text-[12px] tabular text-muted-foreground lg:block">{relTime(o.updated_at)}</div>
                <div className="flex lg:justify-end">
                  {o.linked_opportunity_id ? (
                    <Button variant="outline" size="sm" onClick={() => navigate(`/opportunities/${o.linked_opportunity_id}`)} data-testid="intel-opps-open-linked-button">Open <ArrowUpRight className="h-3.5 w-3.5" /></Button>
                  ) : o.status === "open" ? (
                    <Button variant="soft" size="sm" onClick={() => handoff(o)} disabled={busy === o.id} data-testid="intel-opps-handoff-button"><Kanban className="h-3.5 w-3.5" /> Hand off</Button>
                  ) : <span className="text-[12px] text-muted-foreground">–</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </PageContainer>
  );
}
